import React, { useState, useCallback, useMemo } from 'react';
import { useDevices } from '../../context/DeviceContext';
import {
  DEVICE_STATUS,
  STATUS_LABEL,
  VALVE_STATE,
  deriveStatus,
  deriveValveView,
  formatLastSeen,
} from '../../domain/device';
import ValveSchedules from '../../components/ValveSchedules/ValveSchedules';
import { supportsValveCommandProtocol } from '../../domain/valveCommand';
import authService from '../../services/auth';
import './ValveControl.css';

const ValveSVG = ({ state, isAnimating }) => {
  // Determina el ángulo de la manija y el flujo según el estado.
  // El chorro se escala en lugar de cambiar de ancho: animar `width` obliga al
  // navegador a recalcular la geometría en cada fotograma; `transform` no.
  let handleAngle = 0;
  let flowScale = 0;
  let flowOpacity = 0;
  const flowX = 70;
  const flowMaxWidth = 120;

  if (state === 'open') {
    handleAngle = 90;
    flowScale = 1;
    flowOpacity = 1;
  } else if (state === 'semi-open') {
    handleAngle = 45;
    flowScale = 0.5;
    flowOpacity = 0.7;
  }

  /* Todo el dibujo sale del sistema de color, así que funciona igual en claro
     y en oscuro. El único acento es el agua cuando de verdad pasa agua. */
  const tubeColor = 'var(--border-strong)';
  const tubeFill = 'var(--bg-sunken)';
  const bodyStroke = 'var(--border-strong)';
  const bodyFill = 'var(--bg-surface)';
  const ballFill = 'var(--bg-inset)';
  const handleFill = isAnimating ? 'var(--warning)' : 'var(--text-secondary)';
  const handleKnob = isAnimating ? 'var(--warning)' : 'var(--text-muted)';
  const shadow = 'var(--text-primary)';
  const flowColor = 'var(--data-water)';

  const clases = [
    'valve-svg',
    isAnimating ? 'animating' : '',
    state === 'unknown' ? 'is-unknown' : '',
  ].filter(Boolean).join(' ');

  return (
    <svg width="320" height="140" viewBox="0 0 320 140" className={clases}>
      {/* Tubo izquierdo */}
      <rect x="20" y="60" width="80" height="20" fill={tubeFill} stroke={tubeColor} strokeWidth="3" rx="10" />
      {/* Tubo derecho */}
      <rect x="220" y="60" width="80" height="20" fill={tubeFill} stroke={tubeColor} strokeWidth="3" rx="10" />
      {/* Flujo */}
      <rect
        x={flowX}
        y="68"
        width={flowMaxWidth}
        height="4"
        fill={flowColor}
        opacity={flowOpacity}
        style={{
          transform: `scaleX(${flowScale})`,
          transformOrigin: `${flowX}px 70px`,
          transition: 'transform 0.7s, opacity 0.7s',
        }}
        rx="2"
      />
      {/* Cuerpo de la válvula */}
      <ellipse cx="160" cy="70" rx="50" ry="32" fill={bodyFill} stroke={bodyStroke} strokeWidth="3" />
      {/* Bola interna */}
      <ellipse cx="160" cy="70" rx="28" ry="28" fill={ballFill} stroke={tubeColor} strokeWidth="2" />
      {/* Manija */}
      <g transform={`rotate(${handleAngle} 160 40)`} style={{ transition: 'transform 0.7s' }}>
        <rect x="150" y="18" width="20" height="50" fill={handleFill} rx="5" />
        <rect x="155" y="8" width="10" height="14" fill={handleKnob} rx="3" />
        <circle cx="160" cy="40" r="7" fill={bodyFill} stroke={bodyStroke} strokeWidth="2" />
      </g>
      {/* Eje */}
      <rect x="156" y="46" width="8" height="24" fill={tubeColor} rx="3" />
      {/* Sombra */}
      <ellipse cx="160" cy="110" rx="38" ry="8" fill={shadow} opacity="0.06" />
    </svg>
  );
};

/**
 * Control de válvula del dispositivo seleccionado.
 *
 * Esta pantalla estaba entera fingida: `useState('closed')`, un `deviceId`
 * escrito a mano, un `setTimeout` de 800 ms haciendo de red y un badge fijo en
 * "Conectado · Modo demo". Nunca llamó a AWS. En una cuenta real eso significa
 * que pulsar "Cerrar válvula" no cerraba nada y aun así lo daba por hecho -lo
 * más peligroso que puede hacer la interfaz de un corte de agua-, y que el
 * estado mostrado no era el del equipo: los dos dispositivos reales tienen la
 * válvula ABIERTA en AWS mientras la pantalla decía "Cerrada".
 *
 * Ahora todo sale del dispositivo seleccionado y de lo que él mismo reporta.
 */
const ValveControl = () => {
  const {
    selectedDevice,
    isAccountScope,
    controlSelectedDeviceValve,
    valveCommand,
    clearValveCommand,
    updateSelectedDeviceConfig,
  } = useDevices();

  const [errorMessage, setErrorMessage] = useState(null);

  const valvula = useMemo(() => deriveValveView(selectedDevice), [selectedDevice]);
  const estadoEquipo = useMemo(() => deriveStatus(selectedDevice), [selectedDevice]);
  const programaciones = useMemo(() => {
    let configuration = selectedDevice?.configuration;
    if (typeof configuration === 'string') {
      try {
        configuration = JSON.parse(configuration);
      } catch {
        configuration = {};
      }
    }
    return configuration?.valve_schedules ?? [];
  }, [selectedDevice]);

  // El aviso sólo vale si es de ESTE dispositivo: el contexto lo ata a su
  // device_id precisamente para que no se arrastre al cambiar de equipo.
  const comando =
    valveCommand && valveCommand.deviceId === selectedDevice?.device_id ? valveCommand : null;
  const enCurso = comando?.phase === 'sending' || comando?.phase === 'awaiting';

  const enviar = useCallback(
    async (action) => {
      if (enCurso || !selectedDevice) return;
      setErrorMessage(null);
      try {
        await controlSelectedDeviceValve(action);
      } catch (err) {
        setErrorMessage(err.message || 'No se pudo enviar el comando al dispositivo.');
      }
    },
    [enCurso, selectedDevice, controlSelectedDeviceValve]
  );

  const guardarProgramaciones = useCallback(
    (valveSchedules) => updateSelectedDeviceConfig({
      configuration: { valve_schedules: valveSchedules },
    }),
    [updateSelectedDeviceConfig]
  );

  // Sin dispositivo elegido no hay válvula que controlar. Antes la pantalla
  // resolvía esto inventándose `nissa-guard-001`, que podía no ser ni suyo.
  if (isAccountScope || !selectedDevice) {
    return (
      <div className="container">
        <div className="valve-grid-card valve-empty">
          <h3>Selecciona un dispositivo</h3>
          <p>
            Cada dispositivo tiene su propia válvula. Elige uno en el selector superior para ver y
            controlar la suya.
          </p>
        </div>
      </div>
    );
  }

  let estadoVisual = 'unknown';
  if (!enCurso && valvula.state === VALVE_STATE.OPEN) estadoVisual = 'open';
  else if (!enCurso && valvula.state === VALVE_STATE.CLOSED) estadoVisual = 'closed';

  const caudal = Number(selectedDevice.flow_rate_lpm ?? 0);
  const bloqueado = !valvula.controllable;

  return (
    <div className="container">
      <div className="valve-grid-card">
        {/* Estado de conexión REAL del equipo, el mismo que en el panel */}
        <div className={`connection-status is-${estadoEquipo}`}>
          <span className="status-dot" />
          <span className="status-text">{STATUS_LABEL[estadoEquipo]}</span>
          <span className="last-update">
            {estadoEquipo === DEVICE_STATUS.NEVER_SEEN
              ? '· Aún no envía datos'
              : `· Última lectura ${formatLastSeen(selectedDevice)}`}
          </span>
        </div>

        {/* Visualización de la válvula */}
        <div className="valveContainer">
          <ValveSVG state={estadoVisual} isAnimating={enCurso} />
        </div>

        {/* Estado actual */}
        <div className="statusContainer">
          <span className="statusLabel">Estado reportado por el relé</span>
          <span className={`statusValue is-${valvula.state}`}>
            {enCurso ? 'Enviando…' : valvula.label}
          </span>

          {/* La distinción que faltaba: lo guardado en AWS es el último estado
              que el equipo alcanzó a reportar, no necesariamente el de ahora. */}
          {!enCurso && valvula.known && !valvula.live && (
            <span className="stateCaveat">
              Último estado eléctrico confirmado · {valvula.lastReport}
            </span>
          )}
          {!enCurso && valvula.state === VALVE_STATE.UNKNOWN && (
            <span className="stateCaveat">El dispositivo aún no ha reportado su válvula.</span>
          )}
          {!enCurso && valvula.live && valvula.state === VALVE_STATE.OPEN && caudal > 0 && (
            <span className="flowInfo">Flujo actual · {caudal.toFixed(2)} L/min</span>
          )}
        </div>

        {/* Resultado del último comando */}
        {comando?.phase === 'awaiting' && (
          <div className="valve-notice is-pending">
            Comando enviado. Esperando a que el dispositivo confirme el cambio en su próxima
            telemetría…
          </div>
        )}
        {comando?.phase === 'confirmed' && (
          <div className="valve-notice is-ok">
            <span>
              {comando.confirmationMode === 'legacy_telemetry'
                ? 'Una telemetría posterior muestra el relé en posición de '
                : 'El dispositivo confirmó el relé en posición de '}
              {comando.target === VALVE_STATE.OPEN ? 'apertura' : 'cierre'}.
            </span>
            <button type="button" className="notice-close" onClick={clearValveCommand}>
              Entendido
            </button>
          </div>
        )}
        {comando?.phase === 'unconfirmed' && (
          <div className="valve-notice is-warn">
            <span>
              El comando salió hacia AWS, pero el dispositivo no ha confirmado el cambio. Se sigue
              mostrando el último estado eléctrico confirmado.
            </span>
            <button type="button" className="notice-close" onClick={clearValveCommand}>
              Entendido
            </button>
          </div>
        )}

        {comando?.phase === 'rejected' && (
          <div className="valve-notice is-warn" role="alert">
            <span>
              {comando.commandStatus === 'expired'
                ? 'El comando venció antes de ejecutarse.'
                : comando.commandStatus === 'unconfirmed'
                  ? 'El dispositivo recibió el comando, pero no pudo confirmar el relé.'
                  : 'El dispositivo no pudo ejecutar el comando.'}
              {' '}Se muestra el último estado eléctrico confirmado.
            </span>
            <button type="button" className="notice-close" onClick={clearValveCommand}>
              Entendido
            </button>
          </div>
        )}

        {bloqueado && (
          <div className="valve-notice is-warn">
            El dispositivo está {STATUS_LABEL[estadoEquipo].toLowerCase()}. Los comandos se publican
            por MQTT y no quedan guardados para más tarde, así que no llegarían.
          </div>
        )}

        {/* Mensaje de error */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}

        {/* Controles */}
        <div className="controlsContainer">
          <div className="button-group">
            <button
              className={`valve-button open ${valvula.state === VALVE_STATE.OPEN ? 'active' : ''}`}
              onClick={() => enviar('open')}
              disabled={enCurso || bloqueado || valvula.state === VALVE_STATE.OPEN}
            >
              <span className="button-text">Abrir válvula</span>
            </button>

            <button
              className={`valve-button close ${valvula.state === VALVE_STATE.CLOSED ? 'active' : ''}`}
              onClick={() => enviar('close')}
              disabled={enCurso || bloqueado || valvula.state === VALVE_STATE.CLOSED}
            >
              <span className="button-text">Cerrar válvula</span>
            </button>
          </div>

        </div>
      </div>

      {/* Información adicional */}
      <div className="info-section">
        <h3>Información</h3>
        <ul>
          <li><strong>Abrir válvula:</strong> permite el paso del agua.</li>
          <li><strong>Cerrar válvula:</strong> detiene completamente el flujo.</li>
          <li>El estado se confirma con el propio dispositivo antes de darlo por hecho.</li>
          <li>Dispositivo: <code>{selectedDevice.device_id}</code></li>
        </ul>
      </div>

      <ValveSchedules
        key={selectedDevice.device_id}
        schedules={programaciones}
        onSave={guardarProgramaciones}
        disabled={!authService.isDemo() && !supportsValveCommandProtocol(selectedDevice)}
        disabledReason={!authService.isDemo() && !supportsValveCommandProtocol(selectedDevice)
          ? 'Para configurar horarios, actualiza el firmware y conecta el dispositivo hasta que reporte la versión compatible.'
          : null}
      />
    </div>
  );
};

export default ValveControl;
